import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing, Check } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { fetchAccountNotices, markNoticeRead, AccountNotice } from '@/lib/email';
import { money } from '@/data/constants';

/**
 * DASHBOARD MIRROR OF CRITICAL EMAILS
 * ---------------------------------------------------------------------------
 * Account activation, payment confirmation, membership activation, credits
 * assigned and lead unlocks are written to mkt_notifications at the same
 * moment the transactional email is sent (see the send-transactional-email
 * edge function). This panel renders that same information inside GigGone, so
 * a customer is NEVER dependent on email delivery to know that their payment
 * landed, their membership is live or their credits arrived.
 */
const AccountNotices: React.FC<{ limit?: number }> = ({ limit = 6 }) => {
  const { lang, path } = useI18n();
  const { profile } = useAuth();
  const es = lang === 'es';
  const [notices, setNotices] = useState<AccountNotice[]>([]);

  useEffect(() => {
    if (!profile?.id) return;
    fetchAccountNotices(profile.id, limit).then(setNotices);
  }, [profile?.id, limit]);

  if (!profile?.id || notices.length === 0) return null;

  const dismiss = async (n: AccountNotice) => {
    await markNoticeRead(n.id);
    setNotices((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
  };

  return (
    <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-900">
          <BellRing className="h-4 w-4 text-orange-500" />
          {es ? 'Avisos de tu cuenta' : 'Your account notices'}
        </h2>
        <span className="text-xs text-slate-400">
          {es ? 'Misma información que enviamos por correo' : 'The same information we email you'}
        </span>
      </div>

      <ul className="mt-3 space-y-2">
        {notices.map((n) => (
          <li
            key={n.id}
            className={`rounded-xl border p-3 ${n.read_at ? 'border-slate-100 bg-white' : 'border-orange-200 bg-orange-50/50'}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-extrabold text-slate-900">{es ? n.title_es : n.title_en}</p>
                <p className="mt-0.5 text-sm text-slate-600">{es ? n.body_es : n.body_en}</p>
                {typeof n.amount === 'number' && n.amount > 0 && (
                  <p className="mt-1 text-sm font-extrabold text-slate-900">{money(n.amount)}</p>
                )}
                <p className="mt-1 text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {n.cta_path && (
                  <Link
                    to={path(n.cta_path)}
                    className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-800"
                  >
                    {es ? 'Abrir' : 'Open'}
                  </Link>
                )}
                {!n.read_at && (
                  <button
                    onClick={() => dismiss(n)}
                    title={es ? 'Marcar como visto' : 'Mark as seen'}
                    className="rounded-lg border border-slate-300 p-1.5 text-slate-500 hover:bg-white"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default AccountNotices;
